import { useState } from 'react'

type Tab = 'sharpe' | 'sortino' | 'calmar' | 'rmultiple'

function grade(v: number, t1: number, t2: number, labels: [string, string, string]) {
  if (v >= t1) return { text: labels[0], cls: 'good' }
  if (v >= t2) return { text: labels[1], cls: 'warn' }
  return { text: labels[2], cls: 'bad' }
}

type SliderRowProps = {
  label: string
  value: number
  min: number
  max: number
  step: number
  unit?: string
  format?: (v: number) => string
  onChange: (v: number) => void
}

function SliderRow({ label, value, min, max, step, unit = '', format, onChange }: SliderRowProps) {
  const display = format ? format(value) : `${value}${unit}`
  return (
    <div className="risk-calc__slider-row">
      <span className="risk-calc__slider-label">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(+e.target.value)}
      />
      <span className="risk-calc__slider-val">{display}</span>
    </div>
  )
}

export function RiskMetricsCalculator() {
  const [tab, setTab] = useState<Tab>('sharpe')
  const [sharpe, setSharpe] = useState({ ret: 24, rf: 0.5, std: 18 })
  const [sortino, setSortino] = useState({ ret: 24, rf: 0.5, dd: 10 })
  const [calmar, setCalmar] = useState({ ret: 24, mdd: 15 })
  const [rmul, setRmul] = useState({ risk: 20, rew: 60, pnl: 45 })

  const sharpeVal = sharpe.std ? ((sharpe.ret - sharpe.rf) / sharpe.std) : 0
  const sharpeGrade = grade(sharpeVal, 2, 1, ['優秀（2.0+）', '良好（1.0–2.0）', '要改善（1.0未満）'])

  const sortinoVal = sortino.dd ? ((sortino.ret - sortino.rf) / sortino.dd) : 0
  const sortinoGrade = grade(sortinoVal, 2, 1, ['優秀（2.0+）', '良好（1.0–2.0）', '要改善（1.0未満）'])
  const sortinoDiff = sortinoVal - sharpeVal

  const calmarVal = calmar.mdd ? (calmar.ret / calmar.mdd) : 0
  const calmarGrade = grade(calmarVal, 1, 0.5, ['優秀（1.0+）', '良好（0.5–1.0）', '要改善（0.5未満）'])
  const calmarRec = calmar.ret ? (calmar.mdd / calmar.ret) : 0

  const rVal = rmul.risk ? (rmul.pnl / rmul.risk) : 0
  const rPlan = rmul.risk ? (rmul.rew / rmul.risk) : 0
  const rGrade = grade(rVal, 2, 1, ['Good（2R+）', 'Acceptable（1R+）', 'Under-performance'])

  const tabs: { id: Tab; label: string }[] = [
    { id: 'sharpe', label: 'Sharpe比' },
    { id: 'sortino', label: 'Sortino比' },
    { id: 'calmar', label: 'Calmar比' },
    { id: 'rmultiple', label: 'R-multiple' },
  ]

  return (
    <div className="risk-calc">
      <p className="risk-calc__lead">
        既存KPI（勝率・PF）では見えない「本当のリスク調整後リターン」を4つの指標で体感してください。
      </p>

      <div className="risk-calc__tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`risk-calc__tab${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'sharpe' && (
        <div className="risk-calc__panel">
          <p className="risk-calc__sec-title">ボラティリティ全体に対するリターン</p>
          <div className="risk-calc__slider-group">
            <SliderRow label="年率リターン" value={sharpe.ret} min={-20} max={100} step={1} unit="%" onChange={v => setSharpe(p => ({ ...p, ret: v }))} />
            <SliderRow label="リスクフリーレート" value={sharpe.rf} min={0} max={5} step={0.1} unit="%" onChange={v => setSharpe(p => ({ ...p, rf: v }))} />
            <SliderRow label="標準偏差（ボラ）" value={sharpe.std} min={1} max={60} step={1} unit="%" onChange={v => setSharpe(p => ({ ...p, std: v }))} />
          </div>
          <div className="risk-calc__results">
            <div className="risk-calc__card">
              <span className="risk-calc__card-label">Sharpe比</span>
              <span className={`risk-calc__card-value ${sharpeGrade.cls}`}>{sharpeVal.toFixed(2)}</span>
              <span className="risk-calc__card-sub">{sharpeGrade.text}</span>
            </div>
            <div className="risk-calc__card">
              <span className="risk-calc__card-label">超過リターン</span>
              <span className={`risk-calc__card-value ${(sharpe.ret - sharpe.rf) >= 0 ? 'good' : 'bad'}`}>
                {(sharpe.ret - sharpe.rf) > 0 ? '+' : ''}{(sharpe.ret - sharpe.rf).toFixed(1)}%
              </span>
              <span className="risk-calc__card-sub">リスクフリーを上回る分</span>
            </div>
          </div>
          <p className="risk-calc__insight">
            {sharpeVal >= 2
              ? 'ボラティリティに対して非常に効率的なリターンを上げています。機関投資家水準です。'
              : sharpeVal >= 1
              ? '市場平均的な効率性です。さらなる改善余地があります。'
              : 'リターンに対してリスクが大きすぎます。ロットサイズやSL設定を見直してください。'}
          </p>
        </div>
      )}

      {tab === 'sortino' && (
        <div className="risk-calc__panel">
          <p className="risk-calc__sec-title">下方リスク（負の変動）だけに対するリターン</p>
          <div className="risk-calc__slider-group">
            <SliderRow label="年率リターン" value={sortino.ret} min={-20} max={100} step={1} unit="%" onChange={v => setSortino(p => ({ ...p, ret: v }))} />
            <SliderRow label="リスクフリーレート" value={sortino.rf} min={0} max={5} step={0.1} unit="%" onChange={v => setSortino(p => ({ ...p, rf: v }))} />
            <SliderRow label="下方偏差（負の変動のみ）" value={sortino.dd} min={1} max={60} step={1} unit="%" onChange={v => setSortino(p => ({ ...p, dd: v }))} />
          </div>
          <div className="risk-calc__results">
            <div className="risk-calc__card">
              <span className="risk-calc__card-label">Sortino比</span>
              <span className={`risk-calc__card-value ${sortinoGrade.cls}`}>{sortinoVal.toFixed(2)}</span>
              <span className="risk-calc__card-sub">{sortinoGrade.text}</span>
            </div>
            <div className="risk-calc__card">
              <span className="risk-calc__card-label">Sharpeとの差</span>
              <span className={`risk-calc__card-value ${sortinoDiff >= 0 ? 'good' : 'bad'}`}>
                {sortinoDiff > 0 ? '+' : ''}{sortinoDiff.toFixed(2)}
              </span>
              <span className="risk-calc__card-sub">プラス＝上昇変動が多い</span>
            </div>
          </div>
          <p className="risk-calc__insight">
            {sortinoVal > sharpeVal
              ? 'Sortino > Sharpeです。上昇変動が多く、下落リスクが抑制されています。優秀な非対称リターン構造です。'
              : sortinoVal === sharpeVal
              ? 'SortinoとSharpeが同等です。上昇・下落が対称的な動きをしています。'
              : 'Sortino < Sharpeです。下落側の変動が上昇側より大きい。下値リスク管理の強化が必要です。'}
          </p>
        </div>
      )}

      {tab === 'calmar' && (
        <div className="risk-calc__panel">
          <p className="risk-calc__sec-title">最大ドローダウンに対するリターン（回復力）</p>
          <div className="risk-calc__slider-group">
            <SliderRow label="年率リターン" value={calmar.ret} min={1} max={100} step={1} unit="%" onChange={v => setCalmar(p => ({ ...p, ret: v }))} />
            <SliderRow label="最大ドローダウン" value={calmar.mdd} min={1} max={80} step={1} unit="%" onChange={v => setCalmar(p => ({ ...p, mdd: v }))} />
          </div>
          <div className="risk-calc__results">
            <div className="risk-calc__card">
              <span className="risk-calc__card-label">Calmar比</span>
              <span className={`risk-calc__card-value ${calmarGrade.cls}`}>{calmarVal.toFixed(2)}</span>
              <span className="risk-calc__card-sub">{calmarGrade.text}</span>
            </div>
            <div className="risk-calc__card">
              <span className="risk-calc__card-label">DDからの回復に要する年数</span>
              <span className={`risk-calc__card-value ${calmarRec <= 1 ? 'good' : calmarRec <= 2 ? 'warn' : 'bad'}`}>
                {calmarRec.toFixed(1)}年
              </span>
              <span className="risk-calc__card-sub">現リターンが続いた場合</span>
            </div>
          </div>
          <p className="risk-calc__insight">
            {calmarVal >= 1
              ? '最大DDに対して年率リターンが上回っています。資金が底をついてもリカバリーが1年以内です。'
              : calmarVal >= 0.5
              ? `回復には${calmarRec.toFixed(1)}年かかります。ドローダウン管理をさらに意識してください。`
              : `リターンに対してドローダウンが深すぎます。最大DDを${Math.round(calmar.ret * 0.8)}%以下に抑えることを目標にしてください。`}
          </p>
        </div>
      )}

      {tab === 'rmultiple' && (
        <div className="risk-calc__panel">
          <p className="risk-calc__sec-title">リスク1単位に対する実際の損益</p>
          <div className="risk-calc__slider-group">
            <SliderRow label="リスク（SL幅）" value={rmul.risk} min={5} max={100} step={5} unit=" pips" onChange={v => setRmul(p => ({ ...p, risk: v }))} />
            <SliderRow label="リワード（TP幅）" value={rmul.rew} min={5} max={300} step={5} unit=" pips" onChange={v => setRmul(p => ({ ...p, rew: v }))} />
            <SliderRow
              label="実際の損益"
              value={rmul.pnl}
              min={-100}
              max={200}
              step={5}
              format={v => `${v > 0 ? '+' : ''}${v} pips`}
              onChange={v => setRmul(p => ({ ...p, pnl: v }))}
            />
          </div>
          <div className="risk-calc__results">
            <div className="risk-calc__card">
              <span className="risk-calc__card-label">R-multiple（実現値）</span>
              <span className={`risk-calc__card-value ${rGrade.cls}`}>{rVal.toFixed(2)}R</span>
              <span className="risk-calc__card-sub">{rGrade.text}</span>
            </div>
            <div className="risk-calc__card">
              <span className="risk-calc__card-label">計画RRR</span>
              <span className="risk-calc__card-value">{rPlan.toFixed(2)}R</span>
              <span className="risk-calc__card-sub">TP÷SL</span>
            </div>
          </div>
          <p className="risk-calc__insight">
            {rVal >= rPlan
              ? `計画RRR（${rPlan.toFixed(2)}R）を上回る実現値です。エグジットが優秀です。`
              : rVal >= 1
              ? `利益は出ていますが計画（${rPlan.toFixed(2)}R）に届いていません。早期エグジットの癖がないか確認を。`
              : rVal > 0
              ? 'マイナスではありませんが1R未満です。リスクを取った割に報われていません。'
              : `マイナスです（R=${rVal.toFixed(2)}）。このパターンのSLとTPの設計を見直してください。`}
          </p>
        </div>
      )}
    </div>
  )
}
